import { db } from "@/common/lib/firebase/firebase"
import {
    keepPreviousData,
    useMutation,
    useQuery,
    useQueryClient,
} from "@tanstack/react-query"
import {
    Timestamp,
    addDoc,
    arrayUnion,
    collection,
    doc,
    getDoc,
    getDocs,
    query,
    setDoc,
    where,
    writeBatch,
    increment,
} from "firebase/firestore"
import {
    getDownloadURL,
    getStorage,
    ref,
    uploadBytesResumable,
} from "firebase/storage"
import { queryClient } from "./tsQueryConfig"
import { UserData } from "./userQueries"
import toast from "react-hot-toast"
import { sendAcceptEmail, sendDeclineEmail } from "./mailService"
import { produce } from "immer"

// Utility Functions
function capitalizeWords(str: string) {
    const strLowerCase = str.toLowerCase()
    return strLowerCase
        .split(" ")
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(" ")
}

export type ApplicationComment = {
    applicationId: string | null
    date: {
        seconds: number | null
        nanoseconds: number | null
    } | null
    type: string | null
    body: string | null
    userId: string | null
    userName: string | null
}

export type ApplicationVote = {
    voteId: string
    voteDate: {
        seconds: number
        nanoseconds: number
    }
    applicationId: string
    score: number
    trusteeId: string
}

type ApplicationDate = {
    seconds: number | null
    nanoseconds: number | null
}
export interface GrantApplication {
    healthSubsector: string | null
    applicantNotified: boolean | null
    reviewStatus: string | null
    dateCreated: ApplicationDate | null
    requestedAmount: number | null
    veteransCheck: boolean | null
    country: string | null
    subsector: string | null
    supportingDocsFiles: any[] | null
    territory: string | null
    region: string | null
    estTurnover: string | null
    category: string | null
    votingStatus: string | null
    adminFiles: any[] | null
    sector: string | null
    applicationReason: string | null
    orgURL: string | null
    grantAmount: number | null
    orgAims: string | null
    votes: ApplicationVote[] | null
    socialSubsector: string | null
    comments: ApplicationComment[] | null
    dateDecide: ApplicationDate | null
    userId: string | null
    dateUpdated: ApplicationDate | null
    hearOf: string | null
    educationSubsector: string | null
    receivedGrantBefore: string | null
    dateSubmitted: ApplicationDate | null
    status: string | null
    orgName: string | null
    charityNumber: string | null
    applicationId: string | null
    ageGroup: string | null
    annualAccountFile: any[] | null
    applicantFiles: any[] | null
    isArchived: boolean | null
    dateArchived: ApplicationDate | null
    votingDeadline?: string | null
    lastReminderSent?: ApplicationDate | null
    reminderCount?: number
    declineReason?: string | null
    acceptReason?: string | null
}

interface FetchApplicationsDTO {
    userRole: string
    userId: string
    filters: {
        category?: string
        reviewStatus?: string
        country?: string
        ageGroup?: string
        showArchived?: boolean
        voteRating?: string
    }
}

const createApplicationInput = (
    country: string,
    charityNumber: string,
    userId: string
): GrantApplication => {
    return {
        // visible inputs
        ageGroup: "",
        annualAccountFile: [],
        applicationReason: "",
        estTurnover: "",
        hearOf: "",
        orgAims: "",
        orgName: "",
        orgURL: "",
        receivedGrantBefore: "",
        region: "",
        requestedAmount: 0,
        sector: "",
        status: "incomplete",
        subsector: "",
        educationSubsector: "",
        healthSubsector: "",
        socialSubsector: "",
        supportingDocsFiles: [],
        territory: "",
        veteransCheck: false,
        applicantFiles: null,

        dateCreated: Timestamp.now(),
        dateUpdated: Timestamp.now(),
        dateArchived: null,
        isArchived: false,

        // hidden inputs
        charityNumber: charityNumber,
        country: country,
        userId: userId,

        // only admin will see these fields
        adminFiles: null,
        applicationId: "",
        applicantNotified: false,
        category: "",
        dateDecide: null,
        dateSubmitted: null,
        grantAmount: null,
        reviewStatus: "new",
        votingStatus: null,
        votes: null,
        comments: null,
    }
}

interface UpdateApplicantApplicationDTO {
    ageGroup?: string
    applicationReason?: string
    estTurnover?: string
    hearOf?: string
    orgAims?: string
    orgName?: string
    orgURL?: string
    receivedGrantBefore?: string
    region?: string
    requestedAmount?: number
    sector?: string
    status?: string
    subsector?: string
    educationSubsector?: string
    healthSubsector?: string
    socialSubsector?: string
    territory?: string
    veteransCheck?: boolean
    applicantFiles?: FileLink[]
    dateSubmitted?: { seconds: number; nanoseconds: number } | null
}

// Applicant only functions
export async function createApplication(user: UserData): Promise<string> {
    try {
        const newApplication = createApplicationInput(
            user.country,
            user.charityNumber,
            user.userId
        )
        const applicationRef = await addDoc(
            collection(db, "applications"),
            newApplication
        )
        const applicationId = applicationRef.id

        if (!applicationId) {
            throw new Error("Failed to create application.")
        }

        // update that application with the applicationId
        await setDoc(
            doc(db, "applications", applicationId),
            {
                applicationId,
            },
            { merge: true }
        )

        return applicationId
    } catch (error) {
        throw new Error("Failed to create application. Please try again.")
    }
}

async function fetchApplicantApplicationById(applicationId: string) {
    const applicationRef = doc(db, "applications", applicationId)

    const applicationSnapshot = await getDoc(applicationRef)
    try {
        return applicationSnapshot.data() as GrantApplication
    } catch (error) {
        throw new Error("Failed to fetch application")
    }
}

async function updateApplicantApplication(
    applicationId: string,
    dto: UpdateApplicantApplicationDTO
) {
    const applicationRef = doc(db, "applications", applicationId)

    try {
        await setDoc(applicationRef, dto, { merge: true })
    } catch (error) {
        throw new Error("Failed to update application. Please try again.")
    }
}

export const useUpdateApplicantApplication = (applicationId: string) => {
    return useMutation({
        mutationFn: (dto: UpdateApplicantApplicationDTO) =>
            updateApplicantApplication(applicationId, dto),
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: ["applicantApplication", applicationId],
            })
            toast.success("Application updated successfully")
        },
        onError: () => {
            toast.error("Failed to update application. Please try again.")
        },
    })
}

// TODO: Add error toast
export const useFetchApplicantApplication = (applicationId: string) => {
    return useQuery({
        queryKey: ["applicantApplication", applicationId],
        queryFn: () => fetchApplicantApplicationById(applicationId),
        enabled: !!applicationId,
    })
}

// All user roles functions
async function fetchAdminApplications(
    filters: FetchApplicationsDTO["filters"]
): Promise<GrantApplication[]> {
    const applicationQuery = collection(db, "applications")
    const queryConstraints = []

    // Base status filter
    queryConstraints.push(
        where("status", "in", ["submitted", "reviewed", "accepted"])
    )

    // Category filter
    if (filters.category && filters.category !== "all") {
        queryConstraints.push(where("category", "==", filters.category))
    }

    // Review status filter
    if (filters.reviewStatus && filters.reviewStatus !== "all") {
        queryConstraints.push(where("reviewStatus", "==", filters.reviewStatus))
    }

    // Country filter
    if (filters.country && filters.country !== "all") {
        queryConstraints.push(
            where("country", "==", filters.country.toLowerCase())
        )
    }

    // Age group filter
    if (filters.ageGroup && filters.ageGroup !== "all") {
        queryConstraints.push(where("ageGroup", "==", filters.ageGroup))
    }

    // Get all documents first
    const q = query(applicationQuery, ...queryConstraints)
    const querySnapshot = await getDocs(q)

    // Then filter based on archive status and vote rating in memory
    const applications = querySnapshot.docs
        .map((doc) => doc.data() as GrantApplication) // get the data
        .filter((application) => {
            // filter by criteria
            // Archive filter
            const archiveMatch = filters.showArchived
                ? application.isArchived
                : !application.isArchived

            // Vote rating filter
            let voteRatingMatch = true
            if (filters.voteRating && filters.voteRating !== "all") {
                const votes = application.votes || []
                if (votes.length === 0) {
                    voteRatingMatch = false
                } else {
                    const avgScore =
                        votes.reduce((sum, vote) => sum + vote.score, 0) /
                        votes.length
                    const minRating = parseInt(filters.voteRating)
                    voteRatingMatch = avgScore >= minRating
                }
            }

            return archiveMatch && voteRatingMatch
        })

    return applications
}

// Function to fetch applications for trustees
async function fetchTrusteeApplications(
    filters: FetchApplicationsDTO["filters"]
): Promise<GrantApplication[]> {
    const applicationQuery = query(
        collection(db, "applications"),
        where("reviewStatus", "not-in", ["declined", "new", "accepted"])
    )

    const applicationsSnapshot = await getDocs(applicationQuery)

    let applications = applicationsSnapshot.docs.map((doc) => doc.data())

    if (filters.category && filters.category !== "all") {
        applications = applications.filter(
            (application) =>
                application.category?.toLowerCase() ===
                filters.category?.toLowerCase()
        )
    }

    if (filters.country && filters.country !== "all") {
        applications = applications.filter(
            (application) =>
                application.country?.toLowerCase() ===
                filters.country?.toLowerCase()
        )
    }

    if (filters.ageGroup && filters.ageGroup !== "all") {
        applications = applications.filter(
            (application) =>
                capitalizeWords(application.ageGroup) ===
                capitalizeWords(filters.ageGroup!)
        )
    }

    if (filters.voteRating && filters.voteRating !== "all") {
        applications = applications.filter(
            (application) =>
                application.voteRating?.toLowerCase() ===
                filters.voteRating?.toLowerCase()
        )
    }

    return applications as GrantApplication[]
}

// Function to fetch applications for applicants
async function fetchUserApplications(
    userId: string
): Promise<GrantApplication[]> {
    const applicationQuery = query(
        collection(db, "applications"),
        where("userId", "==", userId)
        // or just get all that have a status
    )

    const applicationsSnapshot = await getDocs(applicationQuery)

    const applications = applicationsSnapshot.docs.map((doc) => doc.data())

    return applications as GrantApplication[]
}

// Function to fetch applications based on user role
async function fetchApplications(
    dto: FetchApplicationsDTO
): Promise<GrantApplication[]> {
    switch (dto.userRole) {
        case "admin":
            return await fetchAdminApplications(dto.filters)
        case "trustee":
            return await fetchTrusteeApplications(dto.filters)
        default:
            return await fetchUserApplications(dto.userId)
    }
}

async function fetchSingleApplication(applicationId: string) {
    const applicationDoc = await getDoc(doc(db, "applications", applicationId))
    const applicationData = applicationDoc.exists()
        ? (applicationDoc.data() as GrantApplication)
        : undefined
    let user: Partial<UserData> | null = null

    if (applicationData?.userId) {
        const userDoc = await getDoc(doc(db, "users", applicationData.userId))
        user = userDoc.exists() ? (userDoc.data() as Partial<UserData>) : null
    }

    return {
        application: applicationData as GrantApplication,
        user,
    }
}

export const useFetchApplications = (dto: FetchApplicationsDTO) => {
    return useQuery({
        queryKey: ["applications", dto.filters],
        queryFn: () => fetchApplications(dto),
        enabled: !!dto.userRole && !!dto.userId,
        placeholderData: keepPreviousData,
        refetchOnMount: "always",
    })
}

export const useFetchApplication = (applicationId: string) => {
    return useQuery({
        queryKey: ["application", applicationId],
        queryFn: () => fetchSingleApplication(applicationId),
        enabled: !!applicationId,
    })
}

async function addCommentToApplication(
    applicationId: string,
    comment: ApplicationComment
) {
    const applicationRef = doc(db, "applications", applicationId)

    try {
        await setDoc(
            applicationRef,
            {
                comments: arrayUnion(comment),
            },
            { merge: true }
        )
    } catch (error) {
        throw new Error("Failed to add comment. Please try again.")
    }
}

export const useAddCommentToApplication = (applicationId: string) => {
    const queryClient = useQueryClient()
    return useMutation({
        mutationFn: (comment: ApplicationComment) =>
            addCommentToApplication(applicationId, comment),
        onSuccess: () => {
            // Single invalidation is sufficient
            queryClient.invalidateQueries({
                queryKey: ["application", applicationId],
            })
            queryClient.refetchQueries({
                queryKey: ["application", applicationId],
                exact: true,
            })
            toast.success("Comment added successfully")
        },
        onError: () => {
            toast.error("Failed to add comment. Please try again.")
        },
    })
}

async function addVoteToApplication(
    applicationId: string,
    vote: ApplicationVote
) {
    const applicationRef = doc(db, "applications", applicationId)
    const trusteeCount = (await fetchTrustees()).length
    const applicationData = (
        await getDoc(applicationRef)
    ).data() as GrantApplication
    try {
        await setDoc(
            applicationRef,
            {
                votes: arrayUnion(vote),
                votingStatus: `${
                    applicationData.votes &&
                    applicationData.votes.length + 1 === trusteeCount
                        ? "complete"
                        : "incomplete"
                }`,
            },
            { merge: true }
        )
    } catch (error) {
        throw new Error("Failed to add vote. Please try again.")
    }
}

export const useAddVoteToApplication = (applicationId: string) => {
    return useMutation({
        mutationFn: (vote: ApplicationVote) =>
            addVoteToApplication(applicationId, vote),
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: ["application", applicationId],
            })
            queryClient.refetchQueries({
                queryKey: ["application", applicationId],
                exact: true,
            })
            toast.success("Vote added successfully")
        },
        onError: () => {
            toast.error("Failed to add vote. Please try again.")
        },
    })
}

export const FILE_MIME_TYPES = [
    "image/png",
    "image/gif",
    "image/jpeg",
    "image/svg+xml",
    "image/webp",
    "application/pdf",
    "application/msword",
    "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    "application/vnd.ms-excel",
    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
] as const

export type FileMimeType = (typeof FILE_MIME_TYPES)[number]
type FileLink = {
    name: string
    path: string
}

// Upload a single file to firebase storage
async function uploadSingleFile(
    file: File,
    applicationId: string
): Promise<FileLink | undefined> {
    if (!FILE_MIME_TYPES.includes(file.type as FileMimeType)) {
        return undefined
    }

    const path = `files/${applicationId}/${file.name}`
    const storage = getStorage()
    const storageRef = ref(storage, path)
    const uploadTask = uploadBytesResumable(storageRef, file)

    return new Promise((resolve, reject) => {
        uploadTask.on(
            "state_changed",
            (snapshot) => {
                const progress = Math.round(
                    (snapshot.bytesTransferred / snapshot.totalBytes) * 100
                )
                console.info(progress)
            },
            (error) => reject(error),
            async () => {
                try {
                    const downloadURL = await getDownloadURL(storageRef)
                    resolve({ name: file.name, path: downloadURL })
                } catch (error) {
                    reject(error)
                }
            }
        )
    })
}

// Function to upload multiple files sequentially
export async function handleFilesUpload(
    files: File[],
    applicationId: string
): Promise<FileLink[]> {
    const fileLinks: FileLink[] = []

    for (const file of files) {
        const fileLink = await uploadSingleFile(file, applicationId)
        if (fileLink) {
            fileLinks.push(fileLink)
        }
    }

    return fileLinks
}

export const useUploadFiles = (applicationId: string) => {
    return useMutation({
        mutationFn: (files: File[]) => handleFilesUpload(files, applicationId),
        onSuccess: async (data) => {
            await updateApplicantApplication(applicationId, {
                applicantFiles: data,
            })
            queryClient.invalidateQueries({
                queryKey: ["applicantApplication", applicationId],
            })
            queryClient.refetchQueries({
                queryKey: ["applicationDocuments", applicationId],
            })
            toast.success("Files uploaded successfully")
        },
        onError: () => {
            toast.error("Failed to upload files. Please try again.")
        },
    })
}

// Download a file from a URL
export async function downloadFile(
    url: string,
    fileName: string
): Promise<void> {
    let blobUrl: string | undefined

    try {
        const response = await fetch(url)
        if (!response.ok) {
            throw new Error("Failed to fetch file")
        }

        const blob = await response.blob()
        blobUrl = URL.createObjectURL(blob)

        const link = document.createElement("a")
        link.href = blobUrl
        link.download = fileName

        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
    } catch (error) {
        throw new Error("Failed to download file")
    } finally {
        if (blobUrl) {
            URL.revokeObjectURL(blobUrl)
        }
    }
}

// Get system trustees
async function fetchTrustees() {
    const trusteesQuery = query(
        collection(db, "users"),
        where("role", "==", "trustee")
    )

    const trusteesSnapshot = await getDocs(trusteesQuery)

    const trustees = trusteesSnapshot.docs
        .map((doc) => doc.data() as UserData)
        .filter((trustee) => !(trustee.inactive ?? false))

    return trustees
}

export const useFetchTrustees = (enabled: boolean = true) => {
    return useQuery({
        queryKey: ["trustees"],
        queryFn: fetchTrustees,
        enabled,
    })
}

async function updateApplicationCategory(
    applicationId: string,
    category: string
) {
    const applicationRef = doc(db, "applications", applicationId)

    const currentReviewStatus = (await getDoc(applicationRef)).data()
        ?.reviewStatus

    try {
        await setDoc(
            applicationRef,
            {
                category,
                reviewStatus:
                    currentReviewStatus === "new" // if the application is new, set the review status to review for trustees to review
                        ? "review"
                        : currentReviewStatus,
            },
            { merge: true }
        )
    } catch (error) {
        throw new Error("Failed to update category. Please try again.")
    }
}

export const useUpdateApplicationCategory = (applicationId: string) => {
    return useMutation({
        mutationFn: (category: string) =>
            updateApplicationCategory(applicationId, category),
        onMutate: async (newCategory: string) => {
            // Fetch the previous data so we can roll-back if our update fails
            const previousData = queryClient.getQueryData<GrantApplication>([
                "application",
                applicationId,
            ])
            // Optimistic update
            queryClient.setQueryData<GrantApplication>(
                ["application", applicationId],
                produce((draft) => {
                    if (draft) draft.category = newCategory
                })
            )

            return { previousData }
        },
        onError: (_, __, context) => {
            // Rollback on error
            if (context?.previousData) {
                queryClient.setQueryData(
                    ["application", applicationId],
                    context.previousData
                )
            }
            toast.error("Failed to update category", {
                id: "category-update",
            })
        },
        onSuccess: (_) => {
            queryClient.invalidateQueries({
                queryKey: ["application", applicationId],
            })
            toast.success("Category updated successfully", {
                id: "category-update",
            })
        },
    })
}

interface ApplicationAcceptStatusUpdate {
    reviewStatus: "accepted"
    grantAmount: number
    acceptReason?: string
}

interface ApplicationDeclineStatusUpdate {
    reviewStatus: "declined"
    declineReason: string
    applicationIds?: string[] // For bulk operations
}

interface ApplicationNewStatusUpdate {
    reviewStatus: "new"
}

interface ApplicationReviewStatusUpdate {
    reviewStatus: "review"
}

type ApplicationStatusUpdate = (
    | ApplicationAcceptStatusUpdate
    | ApplicationDeclineStatusUpdate
    | ApplicationNewStatusUpdate
    | ApplicationReviewStatusUpdate
) & {
    sendEmailNotification?: boolean
}

// Add new function for batch archiving
async function archiveApplications(applicationIds: string[]): Promise<void> {
    const batch = writeBatch(db)

    for (const applicationId of applicationIds) {
        const applicationRef = doc(db, "applications", applicationId)
        batch.update(applicationRef, {
            isArchived: true,
            dateArchived: Timestamp.now(),
        })
    }

    await batch.commit()
    await queryClient.invalidateQueries({ queryKey: ["applications"] })
}

export const useArchiveApplications = () => {
    return useMutation({
        mutationFn: archiveApplications,
        onSuccess: async (_, applicationIds) => {
            toast.success("Applications archived successfully")
            await queryClient.invalidateQueries({ queryKey: ["applications"] })
            await Promise.all(
                applicationIds.map(async (id) => {
                    return queryClient.refetchQueries({
                        queryKey: ["application", id],
                    })
                })
            )
        },
        onError: () => {
            toast.error("Failed to archive applications")
        },
    })
}

function statusUpdateIsAcceptType(
    update: ApplicationStatusUpdate
): update is ApplicationAcceptStatusUpdate {
    return update.reviewStatus === "accepted"
}

function statusUpdateIsDeclineType(
    update: ApplicationStatusUpdate
): update is ApplicationDeclineStatusUpdate {
    return update.reviewStatus === "declined"
}

// function statusUpdateIsNewType(
//     update: ApplicationStatusUpdate
// ): update is ApplicationNewStatusUpdate {
//     return update.reviewStatus === "new"
// }

// function statusUpdateIsReviewType(
//     update: ApplicationStatusUpdate
// ): update is ApplicationReviewStatusUpdate {
//     return update.reviewStatus === "review"
// }

// Update the existing updateApplicationStatus function
async function updateApplicationStatus(
    applicationId: string,
    update: ApplicationStatusUpdate,
    sendEmailNotification: boolean = true
): Promise<void> {
    const applicationRef = doc(db, "applications", applicationId)
    const batch = writeBatch(db)

    try {
        const applicationDoc = await getDoc(applicationRef)
        const application = applicationDoc.data() as GrantApplication
        if (!application.userId) {
            throw new Error("User ID not found")
        }

        const userData = await getDoc(doc(db, "users", application.userId))
        const userEmail = userData.data()?.email

        if (!userEmail) {
            throw new Error("User email not found")
        }

        const now = Timestamp.now()
        const updates: Partial<GrantApplication> = {
            reviewStatus: update.reviewStatus,
            dateDecide: now,
            applicantNotified: false, // Start with false
        }

        // First apply the status update
        if (statusUpdateIsAcceptType(update)) {
            updates.grantAmount = update.grantAmount
            updates.acceptReason = update.acceptReason
        } else if (statusUpdateIsDeclineType(update)) {
            updates.declineReason = update.declineReason
        }

        batch.update(applicationRef, updates)
        await batch.commit()

        // Then attempt to send email
        let emailSent = false
        if (sendEmailNotification) {
            if (statusUpdateIsAcceptType(update)) {
                await sendAcceptEmail([
                    {
                        email: userEmail,
                        grantAmount: update.grantAmount,
                        acceptReason: update.acceptReason,
                    },
                ])
                emailSent = true
            } else if (statusUpdateIsDeclineType(update)) {
                await sendDeclineEmail([
                    {
                        email: userEmail,
                        declineReason: update.declineReason,
                    },
                ])
                emailSent = true
            }
        }

        // Update applicantNotified based on email success
        if (emailSent) {
            await setDoc(
                applicationRef,
                {
                    applicantNotified: true,
                },
                { merge: true }
            )
        }

        queryClient.refetchQueries({ queryKey: ["application", applicationId] })
        queryClient.invalidateQueries({ queryKey: ["applications"] })
    } catch (error) {
        throw error
    }
}

async function updateMultipleApplicationStatus(
    applicationIds: string[],
    update: ApplicationStatusUpdate
): Promise<void> {
    const batch = writeBatch(db)
    const now = Timestamp.now()

    try {
        // Process all applications in parallel for efficiency
        const applications = await Promise.all(
            applicationIds.map(async (id) => {
                const docRef = await getDoc(doc(db, "applications", id))
                if (!docRef.exists()) {
                    throw new Error(`Application ${id} not found`)
                }
                return { id, data: docRef.data() as GrantApplication }
            })
        )

        // Get all user emails in parallel
        const userDocs = await Promise.all(
            applications.map(async ({ data }) => {
                if (!data.userId) throw new Error("User ID not found")
                const userDoc = await getDoc(doc(db, "users", data.userId))
                return { email: userDoc.data()?.email, userId: data.userId }
            })
        )

        // First apply all status updates
        applications.forEach(({ id }) => {
            const applicationRef = doc(db, "applications", id)
            const updates: Partial<GrantApplication> = {
                reviewStatus: update.reviewStatus,
                dateDecide: now,
                applicantNotified: false, // Start with false
            }

            if (statusUpdateIsAcceptType(update)) {
                updates.grantAmount = update.grantAmount
            }

            batch.update(applicationRef, updates)
        })

        await batch.commit()

        // Then attempt to send all emails
        const emailResults = await Promise.allSettled(
            applications.map(async (_, index) => {
                const userEmail = userDocs[index].email
                if (!userEmail) return false

                try {
                    if (statusUpdateIsAcceptType(update)) {
                        await sendAcceptEmail([
                            {
                                email: userEmail,
                                grantAmount: update.grantAmount,
                                acceptReason: update.acceptReason,
                            },
                        ])
                        return true
                    } else if (statusUpdateIsDeclineType(update)) {
                        await sendDeclineEmail([
                            {
                                email: userEmail,
                                declineReason: update.declineReason,
                            },
                        ])
                        return true
                    }
                } catch {
                    return false
                }
                return false
            })
        )

        // Update applicantNotified based on email success
        const notificationBatch = writeBatch(db)
        applications.forEach(({ id }, index) => {
            if (emailResults[index].status === "fulfilled") {
                const applicationRef = doc(db, "applications", id)
                notificationBatch.update(applicationRef, {
                    applicantNotified: true,
                })
            }
        })
        await notificationBatch.commit()

        // Invalidate queries and refetch
        await Promise.all([
            queryClient.invalidateQueries({ queryKey: ["applications"] }),
            ...applicationIds.map((id) =>
                queryClient.refetchQueries({ queryKey: ["application", id] })
            ),
        ])
    } catch (error) {
        throw error
    }
}

export const useUpdateApplicationStatus = (applicationId: string) => {
    return useMutation({
        mutationFn: async (update: ApplicationStatusUpdate) => {
            if (statusUpdateIsDeclineType(update) && update.applicationIds) {
                await updateMultipleApplicationStatus(
                    update.applicationIds,
                    update
                )
            } else {
                await updateApplicationStatus(
                    applicationId,
                    update,
                    update.sendEmailNotification
                )
            }
        },
    })
}

// ADMIN FUNCTIONS FOR MIGRATIONS
// export interface MigrationResult {
//     totalItems: number
//     applicationsUpdated: number
//     orphanedItems: number
// }

// export interface VerificationResult {
//     originalCounts: {
//         votes: number
//         comments: number
//     }
//     migratedCounts: {
//         votes: number
//         comments: number
//     }
//     applications: number
//     orphanedData: {
//         votes: string[]
//         comments: string[]
//     }
//     inconsistencies: {
//         applicationsWithoutArrays: string[]
//         applicationsWithEmptyArrays: string[]
//     }
// }

// export async function mergeVotesToApplications(): Promise<MigrationResult> {
//     try {
//         const votesSnapshot = await getDocs(collection(db, 'votes'))
//         const applicationsSnapshot = await getDocs(
//             collection(db, 'applications')
//         )
//         const batch = writeBatch(db)
//         const applicationsVotesMap = new Map<string, ApplicationVote[]>()
//         const applicationIds = new Set(
//             applicationsSnapshot.docs.map((doc) => doc.id)
//         )

//         // First, clear all existing votes arrays
//         applicationsSnapshot.docs.forEach((appDoc) => {
//             const applicationRef = doc(db, 'applications', appDoc.id)
//             batch.update(applicationRef, {
//                 votes: [],
//             })
//         })

//         // Collect all votes with their IDs
//         votesSnapshot.forEach((voteDoc) => {
//             const voteData = voteDoc.data()
//             // Standardize on 'score' field
//             const score =
//                 voteData.score !== undefined ? voteData.score : voteData.vote

//             const vote: ApplicationVote = {
//                 voteId: voteDoc.id,
//                 voteDate: voteData.voteDate,
//                 applicationId: voteData.applicationId,
//                 score: score,
//                 trusteeId: voteData.trusteeId,
//             }

//             if (vote.applicationId) {
//                 if (!applicationsVotesMap.has(vote.applicationId)) {
//                     applicationsVotesMap.set(vote.applicationId, [])
//                 }
//                 // Only add votes with valid score
//                 if (vote.score !== undefined && vote.score !== null) {
//                     applicationsVotesMap.get(vote.applicationId)?.push(vote)
//                 }
//             }
//         })

//         // Update applications with new votes
//         applicationsVotesMap.forEach((votes, applicationId) => {
//             if (applicationIds.has(applicationId)) {
//                 const applicationRef = doc(db, 'applications', applicationId)
//                 batch.update(applicationRef, {
//                     votes: votes,
//                 })
//             }
//         })

//         await batch.commit()
//         console.log('Vote migration completed successfully.')

//         return {
//             totalItems: votesSnapshot.size,
//             applicationsUpdated: applicationIds.size,
//             orphanedItems: Array.from(applicationsVotesMap.keys()).filter(
//                 (id) => !applicationIds.has(id)
//             ).length,
//         }
//     } catch (error) {
//         console.error('Error migrating votes:', error)
//         throw error
//     }
// }

// export async function mergeCommentsToApplications(): Promise<MigrationResult> {
//     try {
//         const commentsSnapshot = await getDocs(collection(db, 'comments'))
//         const applicationsSnapshot = await getDocs(
//             collection(db, 'applications')
//         )
//         const batch = writeBatch(db)
//         const applicationsCommentsMap = new Map<string, ApplicationComment[]>()
//         const applicationIds = new Set(
//             applicationsSnapshot.docs.map((doc) => doc.id)
//         )

//         // First, clear all existing comments arrays
//         applicationsSnapshot.docs.forEach((appDoc) => {
//             const applicationRef = doc(db, 'applications', appDoc.id)
//             batch.update(applicationRef, {
//                 comments: [],
//             })
//         })

//         // Collect all comments with their IDs
//         commentsSnapshot.forEach((commentDoc) => {
//             const commentData = commentDoc.data()

//             // Clean and standardize the comment data
//             const comment: ApplicationComment = {
//                 applicationId: commentData.applicationId || null,
//                 date: commentData.date || null,
//                 // Standardize type: if missing, set to "general"
//                 type: commentData.type || 'general',
//                 // Clean body: trim whitespace, convert empty to null
//                 body: commentData.body?.trim() || null,
//             }

//             // Only add comments with required fields
//             if (comment.applicationId && comment.date) {
//                 if (!applicationsCommentsMap.has(comment.applicationId)) {
//                     applicationsCommentsMap.set(comment.applicationId, [])
//                 }
//                 applicationsCommentsMap
//                     .get(comment.applicationId)
//                     ?.push(comment)
//             }
//         })

//         // Update applications with new comments
//         applicationsCommentsMap.forEach((comments, applicationId) => {
//             if (applicationIds.has(applicationId)) {
//                 const applicationRef = doc(db, 'applications', applicationId)
//                 batch.update(applicationRef, {
//                     comments: comments,
//                 })
//             }
//         })

//         await batch.commit()
//         console.log('Comment migration completed successfully.')

//         return {
//             totalItems: commentsSnapshot.size,
//             applicationsUpdated: applicationIds.size,
//             orphanedItems: Array.from(applicationsCommentsMap.keys()).filter(
//                 (id) => !applicationIds.has(id)
//             ).length,
//         }
//     } catch (error) {
//         console.error('Error migrating comments:', error)
//         throw error
//     }
// }

// export async function verifyMigration(): Promise<VerificationResult> {
//     try {
//         const [applicationsSnapshot, votesSnapshot, commentsSnapshot] =
//             await Promise.all([
//                 getDocs(collection(db, 'applications')),
//                 getDocs(collection(db, 'votes')),
//                 getDocs(collection(db, 'comments')),
//             ])

//         const verification: VerificationResult = {
//             originalCounts: {
//                 votes: votesSnapshot.size,
//                 comments: commentsSnapshot.size,
//             },
//             migratedCounts: {
//                 votes: 0,
//                 comments: 0,
//             },
//             applications: applicationsSnapshot.size,
//             orphanedData: {
//                 votes: [],
//                 comments: [],
//             },
//             inconsistencies: {
//                 applicationsWithoutArrays: [],
//                 applicationsWithEmptyArrays: [],
//             },
//         }

//         // Check each application's votes and comments
//         applicationsSnapshot.docs.forEach((appDoc) => {
//             const data = appDoc.data() as GrantApplication

//             // Check for missing arrays
//             if (!data.votes || !data.comments) {
//                 verification.inconsistencies.applicationsWithoutArrays.push(
//                     appDoc.id
//                 )
//             }

//             // Check for empty arrays
//             if (
//                 Array.isArray(data.votes) &&
//                 data.votes.length === 0 &&
//                 Array.isArray(data.comments) &&
//                 data.comments.length === 0
//             ) {
//                 verification.inconsistencies.applicationsWithEmptyArrays.push(
//                     appDoc.id
//                 )
//             }

//             verification.migratedCounts.votes += (data.votes || []).length
//             verification.migratedCounts.comments += (data.comments || []).length
//         })

//         // Check for orphaned votes and comments
//         votesSnapshot.docs.forEach((voteDoc) => {
//             const vote = voteDoc.data() as ApplicationVote
//             if (
//                 !applicationsSnapshot.docs.find(
//                     (app) => app.id === vote.applicationId
//                 )
//             ) {
//                 verification.orphanedData.votes.push(voteDoc.id)
//             }
//         })

//         commentsSnapshot.docs.forEach((commentDoc) => {
//             const comment = commentDoc.data() as ApplicationComment
//             if (
//                 !applicationsSnapshot.docs.find(
//                     (app) => app.id === comment.applicationId
//                 )
//             ) {
//                 verification.orphanedData.comments.push(commentDoc.id)
//             }
//         })

//         return verification
//     } catch (error) {
//         console.error('Error verifying migration:', error)
//         throw error
//     }
// }

// export async function runMigration() {
//     try {
//         // 1. First verify the current state
//         console.log('Verifying initial state...')
//         const beforeState = await verifyMigration()
//         console.log('Before migration:', beforeState)

//         // 2. Run the migrations
//         console.log('Starting votes migration...')
//         const votesResult = await mergeVotesToApplications()
//         console.log('Votes migration complete:', votesResult)

//         console.log('Starting comments migration...')
//         const commentsResult = await mergeCommentsToApplications()
//         console.log('Comments migration complete:', commentsResult)

//         // 3. Verify the final state
//         console.log('Verifying final state...')
//         const afterState = await verifyMigration()
//         console.log('After migration:', afterState)

//         // 4. Check if migration was successful
//         const success =
//             beforeState.originalCounts.votes ===
//                 afterState.migratedCounts.votes &&
//             beforeState.originalCounts.comments ===
//                 afterState.migratedCounts.comments &&
//             afterState.orphanedData.votes.length === 0 &&
//             afterState.orphanedData.comments.length === 0

//         return {
//             success,
//             beforeState,
//             afterState,
//             votesResult,
//             commentsResult,
//         }
//     } catch (error) {
//         console.error('Migration failed:', error)
//         throw error
//     }
// }

// export async function writeSampleDataToJSON() {
//     try {
//         // Get one user of each role
//         const applicantSnapshot = await getDocs(
//             query(
//                 collection(db, 'users'),
//                 where('role', '==', 'applicant'),
//                 limit(1)
//             )
//         )

//         const adminSnapshot = await getDocs(
//             query(
//                 collection(db, 'users'),
//                 where('role', '==', 'admin'),
//                 limit(1)
//             )
//         )

//         const trusteeSnapshot = await getDocs(
//             query(
//                 collection(db, 'users'),
//                 where('role', '==', 'trustee'),
//                 limit(1)
//             )
//         )

//         // Get one application
//         const applicationSnapshot = await getDocs(
//             query(collection(db, 'applications'), limit(1))
//         )

//         // Get one comment
//         const commentSnapshot = await getDocs(
//             query(collection(db, 'comments'), limit(1))
//         )

//         // Get one vote
//         const voteSnapshot = await getDocs(
//             query(collection(db, 'votes'), limit(1))
//         )

//         const sampleData = {
//             applicantUser: {
//                 id: applicantSnapshot.docs[0]?.id,
//                 ...applicantSnapshot.docs[0]?.data(),
//             },
//             adminUser: {
//                 id: adminSnapshot.docs[0]?.id,
//                 ...adminSnapshot.docs[0]?.data(),
//             },
//             trusteeUser: {
//                 id: trusteeSnapshot.docs[0]?.id,
//                 ...trusteeSnapshot.docs[0]?.data(),
//             },
//             application: {
//                 id: applicationSnapshot.docs[0]?.id,
//                 ...applicationSnapshot.docs[0]?.data(),
//             },
//             comment: {
//                 id: commentSnapshot.docs[0]?.id,
//                 ...commentSnapshot.docs[0]?.data(),
//             },
//             vote: {
//                 id: voteSnapshot.docs[0]?.id,
//                 ...voteSnapshot.docs[0]?.data(),
//             },
//         }

//         // Convert to JSON string with proper formatting
//         const jsonData = JSON.stringify(sampleData, null, 2)

//         // Create and download JSON file
//         const blob = new Blob([jsonData], { type: 'application/json' })
//         const url = window.URL.createObjectURL(blob)
//         const a = document.createElement('a')
//         a.href = url
//         a.download = 'sample_data.json'
//         a.click()
//         window.URL.revokeObjectURL(url)

//         console.log('Sample data has been written to JSON file')
//         return sampleData
//     } catch (error) {
//         console.error('Error writing sample data:', error)
//         return null
//     }
// }

// export async function getApplicationDebugData(applicationId: string) {
//     try {
//         // Get the application
//         const applicationRef = doc(db, 'applications', applicationId)
//         const applicationSnapshot = await getDoc(applicationRef)

//         // Get votes
//         const votesQuery = query(
//             collection(db, 'votes'),
//             where('applicationId', '==', applicationId)
//         )
//         const votesSnapshot = await getDocs(votesQuery)

//         // Get comments
//         const commentsQuery = query(
//             collection(db, 'comments'),
//             where('applicationId', '==', applicationId)
//         )
//         const commentsSnapshot = await getDocs(commentsQuery)

//         console.log('DEBUG DATA FOR:', applicationId)
//         console.log('Application:', applicationSnapshot.data())
//         console.log(
//             'Votes:',
//             votesSnapshot.docs.map((doc) => doc.data())
//         )
//         console.log(
//             'Comments:',
//             commentsSnapshot.docs.map((doc) => doc.data())
//         )
//     } catch (error) {
//         console.error('Error:', error)
//     }
// }

// export async function getCommentsDebugData(applicationId: string) {
//     try {
//         // Get the application
//         const applicationRef = doc(db, 'applications', applicationId)
//         const applicationSnapshot = await getDoc(applicationRef)

//         // Get comments
//         const commentsQuery = query(
//             collection(db, 'comments'),
//             where('applicationId', '==', applicationId)
//         )
//         const commentsSnapshot = await getDocs(commentsQuery)

//         console.log('DEBUG DATA FOR:', applicationId)
//         console.log('Application:', applicationSnapshot.data())
//         console.log(
//             'Comments:',
//             commentsSnapshot.docs.map((doc) => ({
//                 id: doc.id,
//                 ...doc.data(),
//             }))
//         )
//     } catch (error) {
//         console.error('Error:', error)
//     }
// }

// Add unarchive function
async function unarchiveApplications(applicationIds: string[]): Promise<void> {
    const batch = writeBatch(db)

    for (const applicationId of applicationIds) {
        const applicationRef = doc(db, "applications", applicationId)
        batch.update(applicationRef, {
            isArchived: false,
            dateArchived: null,
        })
    }

    await batch.commit()
    await queryClient.invalidateQueries({ queryKey: ["applications"] })
}

export const useUnarchiveApplications = () => {
    return useMutation({
        mutationFn: unarchiveApplications,
        onSuccess: async (_, applicationIds) => {
            toast.success("Applications unarchived successfully")
            await queryClient.invalidateQueries({ queryKey: ["applications"] })
            await Promise.all(
                applicationIds.map(async (id) => {
                    return queryClient.refetchQueries({
                        queryKey: ["application", id],
                    })
                })
            )
        },
        onError: () => {
            toast.error("Failed to unarchive applications")
        },
    })
}

// Add new types for application settings
export interface ApplicationSettings {
    deadline: Timestamp | null
    isAcceptingApplications: boolean
}

// Function to fetch application settings
async function fetchApplicationSettings(): Promise<ApplicationSettings> {
    const settingsRef = doc(db, "applicationSettings", "global")
    const settingsDoc = await getDoc(settingsRef)

    if (!settingsDoc.exists()) {
        // Create default settings if they don't exist
        const defaultSettings: ApplicationSettings = {
            deadline: null,
            isAcceptingApplications: true,
        }
        await setDoc(settingsRef, {
            ...defaultSettings,
            lastUpdated: Timestamp.now(),
        })
        return defaultSettings
    }

    return settingsDoc.data() as ApplicationSettings
}

// Function to update application settings
async function updateApplicationSettings(
    settings?: Partial<ApplicationSettings>
): Promise<void> {
    const settingsRef = doc(db, "applicationSettings", "global")
    await setDoc(
        settingsRef,
        {
            ...settings,
            lastUpdated: Timestamp.now(),
        },
        { merge: true }
    )
}

// Function to schedule deadline using the worker
async function scheduleDeadline(deadline: Date): Promise<void> {
    const baseUrl = import.meta.env.VITE_WORKER_URL
    if (!baseUrl) {
        throw new Error("VITE_WORKER_URL is not set")
    }
    const apiEndpoint = `${baseUrl}/scheduler/schedule`

    const response = await fetch(apiEndpoint, {
        method: "POST",
        headers: {
            "Content-Type": "application/json",
        },
        body: JSON.stringify({ deadline: deadline.toISOString() }),
    })

    if (!response.ok) {
        throw new Error(await response.text())
    }
}

async function clearScheduler(): Promise<void> {
    const baseUrl = import.meta.env.VITE_WORKER_URL
    if (!baseUrl) {
        throw new Error("VITE_WORKER_URL is not set")
    }
    const apiEndpoint = `${baseUrl}/scheduler/clear`

    const response = await fetch(apiEndpoint, {
        method: "POST",
        body: JSON.stringify({ deadline: null }),
    })

    const responseBody = await response.text()

    // If the scheduler is not available, it means the scheduler was not set
    if (responseBody.includes("Scheduler not available")) {
        return
    }

    if (!response.ok) {
        throw new Error(responseBody)
    }
}

// Hook to fetch application settings
export const useApplicationSettings = () => {
    return useQuery({
        queryKey: ["applicationSettings"],
        queryFn: fetchApplicationSettings,
    })
}

// Hook to update application settings
export const useUpdateApplicationSettings = () => {
    const queryClient = useQueryClient()

    return useMutation({
        mutationFn: async ({
            settings,
            scheduleUpdate = false,
        }: {
            settings?: Partial<ApplicationSettings>
            scheduleUpdate?: boolean
        }) => {
            // If we're explicitly setting isAcceptingApplications to false
            if (settings?.isAcceptingApplications === false) {
                // Clear deadline when applications are closed
                await updateApplicationSettings({ ...settings, deadline: null })
                await clearScheduler()
                return
            }
            // If we're setting a new deadline and want to schedule it
            if (scheduleUpdate && settings?.deadline) {
                await scheduleDeadline(settings.deadline.toDate())
                await updateApplicationSettings(settings)
                return
            }

            await updateApplicationSettings(settings)
            // If we're clearing the deadline (settings has deadline: null)
            if (settings?.deadline === null) {
                await clearScheduler()
            }
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["applicationSettings"] })
        },
    })
}

// Add this new function near other application update functions
async function updateApplicationVotingDeadline(
    applicationId: string,
    deadline: string | null,
    isReminder: boolean = false
) {
    const applicationRef = doc(db, "applications", applicationId)

    try {
        const updates: any = {
            dateUpdated: Timestamp.now(),
        }

        if (deadline !== undefined) {
            updates.votingDeadline = deadline
        }

        if (isReminder) {
            updates.lastReminderSent = Timestamp.now()
            updates.reminderCount = increment(1)
        }

        await setDoc(applicationRef, updates, { merge: true })
    } catch (error) {
        throw new Error("Failed to update voting deadline. Please try again.")
    }
}

export const useUpdateVotingDeadline = () => {
    return useMutation({
        mutationFn: ({
            applicationId,
            deadline,
            isReminder = false,
        }: {
            applicationId: string
            deadline?: string | null
            isReminder?: boolean
        }) =>
            updateApplicationVotingDeadline(
                applicationId,
                deadline as string | null,
                isReminder
            ),
        onMutate: async ({ applicationId, deadline, isReminder }) => {
            const previousData = queryClient.getQueryData([
                "application",
                applicationId,
            ])

            queryClient.setQueryData(
                ["application", applicationId],
                (old: any) => ({
                    ...old,
                    application: {
                        ...old.application,
                        ...(deadline !== undefined && {
                            votingDeadline: deadline,
                        }),
                        ...(isReminder && {
                            lastReminderSent: Timestamp.now(),
                            reminderCount:
                                (old.application.reminderCount || 0) + 1,
                        }),
                    },
                })
            )

            return { previousData }
        },
        onError: (_, variables, context) => {
            if (context?.previousData) {
                queryClient.setQueryData(
                    ["application", variables.applicationId],
                    context.previousData
                )
            }
            toast.error("Failed to update voting deadline")
        },
        onSuccess: (_, variables) => {
            queryClient.invalidateQueries({
                queryKey: ["application", variables.applicationId],
            })
            queryClient.invalidateQueries({
                queryKey: ["applications"],
            })
        },
    })
}
